/**
 * Registry API client — the exact HTTP contract of LLM_CONTEXT/02-architecture.md §3
 * (rewritten by Wave 4; this file is its web twin).
 *
 * -----------------------------------------------------------------------
 * WIRE CONTRACT
 * -----------------------------------------------------------------------
 * `version` travels IN THE BODY of every mutation — the registry version the
 * client last read (optimistic CAS, arch §2.2). A stale version answers
 * `409` with the current document in `body.current`; the cache is healed and
 * the UI re-syncs (see `planning-hooks.ts`).
 *
 *   GET    /api/registry
 *          → RegistryDocument                       (503 `registry_missing`
 *                                                    while never seeded)
 *
 *   POST   /api/registry/projects
 *          { version, title, streamId, categoryId?, status?, priority?,
 *            dueDate?, hours?, atRisk?, description?, assignments?: Assignment[] }
 *          → 201 RegistryDocument                   (id is generated when omitted)
 *
 *   PATCH  /api/registry/projects/:id               — FIELD DIFF, not a replace
 *          { version,
 *            set?: { status, hours, … },            — only the CHANGED scalars;
 *                                                   — an absent key = untouched
 *            assignments?: { set: Assignment[] } }  — FULL replacement list
 *          → 200 RegistryDocument
 *
 *          `assignments.set` replaces the WHOLE list (the people picker sends
 *          everything); omitted `assignments` = untouched. Clearing a value
 *          sends an explicit `null` (categoryId/dueDate/hours).
 *
 *   POST   /api/registry/positions                  — manual order of a stream (F-25)
 *          { version, streamId, orderedIds: [...] } — the FULL new order: every
 *                                                     project of the stream exactly
 *                                                     once (position = index + 1)
 *          → 200 RegistryDocument                   (one audit entry per moved
 *                                                     project, all one `ts`)
 *
 *   POST   /api/registry/team-positions             — manual order of a team roster
 *   POST   /api/registry/people-positions           — manual order of a person's queue
 *          { version, teamId | personId, orderedProjectIds: [...] }
 *          → 200 RegistryDocument                   (same full-list contract; the
 *                                                     stamp lands on the team's /
 *                                                     person's assignments)
 *
 *   POST   /api/registry/all-positions              — manual order of the WHOLE
 *          { version, orderedIds: [...] }            project list («Все проекты»,
 *                                                     F-28): every project of the
 *                                                     registry exactly once, so the
 *                                                     order is filter-independent
 *          → 200 RegistryDocument                   (same contract: partial list
 *                                                     400, unknown id 422, no-op 200
 *                                                     without a version bump)
 *
 *   DELETE /api/registry/projects/:id               — version in the BODY
 *          { version }                              (fetch/RequestInit `body`)
 *          → 200 RegistryDocument
 *
 *   POST   /api/registry/{people|teams|streams|categories}
 *          { version, …dictionary fields… }         — people need name + teamId
 *          → 201 RegistryDocument
 *   PATCH  /api/registry/{…}/:id
 *          { version, name?, description?, … }      — FLAT fields (EntityPatch),
 *                                                     `id` is ignored/immutable
 *          → 200 RegistryDocument
 *   DELETE /api/registry/{…}/:id
 *          { version }
 *          → 200 RegistryDocument   (person/team deletes cascade their
 *                                    assignments away — each audited)
 *
 *   POST   /api/registry/statuses             — a CUSTOM status (F-01/F-23)
 *          { version, label }                — `st-…` id, `system: false`
 *          → 201 RegistryDocument
 *   PATCH  /api/registry/statuses/:id         — rename, system ones included
 *          { version, label }
 *          → 200 RegistryDocument
 *   DELETE /api/registry/statuses/:id         — REFUSED (422) for a system
 *          { version }                         status and for a status that
 *                                               projects still use
 *
 *   POST   /api/registry/dictionary-positions — the full new order of ONE
 *          { version, kind, orderedIds }        dictionary (statuses | streams |
 *                                               categories); array order = the
 *                                               display order everywhere
 *          → 200 RegistryDocument             (one `order` audit record per
 *                                               moved entry; no-op 200 without
 *                                               a version bump; partial list 400)
 *
 *   PATCH  /api/registry/streams/:id also accepts
 *          { version, ownerPersonId: string | null }   — «Ответственный»
 *          (unknown person → 422 `invalid_ref`; null clears)
 *
 *   POST   /api/registry/systems                     — managed catalogue (F-21)
 *          { version, name, short?, description?, tags?, url?, repoUrl?,
 *            ownerPersonId? (null = none), archived? }
 *          → 201 RegistryDocument
 *   PATCH  /api/registry/systems/:id                 — flat diff, `archived` toggle
 *          { version, name?, …, ownerPersonId?: string | null }  — null CLEARS
 *          → 200 RegistryDocument   (422 for an unknown person)
 *   DELETE /api/registry/systems/:id
 *          { version }
 *          → 200 RegistryDocument   (422 `invalid_ref` with the linked-project
 *                                    count while projects still link it —
 *                                    archive instead of deleting)
 *
 *   project.set.systemIds (F-21) — the WHOLE list of linked systems replaces
 *   the previous one, exactly like `assignments.set`; every id must exist.
 *
 *   GET    /api/registry/audit?entityId=&actor=&limit=&offset=
 *          → { items: AuditEntry[], total, limit, offset }
 *            (newest first; `total` counts the WHOLE filter, not the page;
 *             `limit` 1..200; `actor` is a case-insensitive substring on
 *             email OR name; entries of ONE mutation share `ts` and are
 *             adjacent in the list)
 * -----------------------------------------------------------------------
 * EVERY successful mutation answers the WHOLE updated document — the client
 * swaps it into the cache and its `version` is the next CAS token.
 *
 * Error envelope (arch §3.3): `400` ValidationPipe format or `{code:'invalid',
 * issues:[{field,message}]}`; `404` `not_found`; `409` `conflict` + `current`;
 * `422` `invalid_ref` + `issues`; `503` `registry_missing` (not seeded).
 */

import { buildQuery, jsonBody, request } from './client';
import type {
  AllPositionsPatch,
  Assignment,
  AuditEntry,
  Category,
  DictionaryPositionsPatch,
  EntityPatch,
  Person,
  PersonPositionsPatch,
  PositionsPatch,
  Priority,
  Project,
  ProjectStatus,
  RegistryDocument,
  Stream,
  SystemEntry,
  Team,
  TeamPositionsPatch,
} from './types';

/** Registry + audit queries share one root so any mutation invalidates both. */
export const QUERY_KEYS = {
  registry: ['registry'] as const,
  audit: ['audit'] as const,
};

/** Scalar project fields a patch may set (`ProjectSetDto`; `id`/timestamps are not settable). */
export type ProjectFieldPatch = Partial<
  Pick<
    Project,
    | 'title'
    | 'streamId'
    | 'categoryId'
    | 'status'
    | 'priority'
    | 'dueDate'
    | 'hours'
    | 'atRisk'
    | 'description'
    | 'systemIds'
  >
>;

/** POST /api/registry/projects — required: title + streamId; the rest have server defaults. */
export interface CreateProjectBody {
  version: number;
  title: string;
  streamId: string;
  categoryId?: string | null;
  status?: ProjectStatus;
  priority?: Priority;
  dueDate?: string | null;
  hours?: number | null;
  atRisk?: boolean;
  description?: string;
  assignments?: Assignment[];
  /** Linked systems (F-21); the server defaults to `[]`. */
  systemIds?: string[];
}

/** PATCH /api/registry/projects/:id — see the contract at the top of this file. */
export interface PatchProjectBody {
  version: number;
  set?: ProjectFieldPatch;
  /** The picker sends the WHOLE list; omit the key to leave assignments untouched. */
  assignments?: { set: Assignment[] };
}

/**
 * POST /api/registry/positions (F-25) — the whole new order of ONE stream.
 * Identical to the shared `PositionsPatch` write protocol.
 */
export type PositionsBody = PositionsPatch;

/**
 * POST /api/registry/team-positions — the whole new order of ONE team's roster
 * (the shared `TeamPositionsPatch` write protocol).
 */
export type TeamPositionsBody = TeamPositionsPatch;

/**
 * POST /api/registry/people-positions — the whole new order of ONE person's
 * own queue (the shared `PersonPositionsPatch` write protocol).
 */
export type PersonPositionsBody = PersonPositionsPatch;

/**
 * POST /api/registry/all-positions (F-28) — the whole new order of the ENTIRE
 * project list (the shared `AllPositionsPatch` write protocol). Scope-free by
 * design: the manual order of «Все проекты» is a property of the full list,
 * never of the subset a filter shows.
 */
export type AllPositionsBody = AllPositionsPatch;

/** DELETE body (projects and dictionaries alike) — version in the body, never in the query. */
export interface DeleteBody {
  version: number;
}

// -- Dictionaries (flat EntityPatch bodies; `id` is immutable) ---------------

/** `{version, …}` — every mutation body carries the CAS token. */
export type Versioned<T> = { version: number } & T;

/** POST /api/registry/people — `name` + `teamId` required (`CreatePersonDto`). */
export type CreatePersonBody = Versioned<Pick<Person, 'name' | 'teamId'> & Partial<Omit<Person, 'id' | 'name' | 'teamId'>>>;

/** PATCH /api/registry/people/:id. */
export type PatchPersonBody = Versioned<EntityPatch<Person>>;

/** POST /api/registry/teams — `name` required. */
export type CreateTeamBody = Versioned<Pick<Team, 'name'> & Partial<Omit<Team, 'id' | 'name'>>>;

/** PATCH /api/registry/teams/:id. */
export type PatchTeamBody = Versioned<EntityPatch<Team>>;

/** POST /api/registry/streams — `name` required. */
export type CreateStreamBody = Versioned<Pick<Stream, 'name'> & Partial<Omit<Stream, 'id' | 'name'>>>;

/** PATCH /api/registry/streams/:id. */
export type PatchStreamBody = Versioned<EntityPatch<Stream>>;

/** POST /api/registry/categories — `name` required. */
export type CreateCategoryBody = Versioned<Pick<Category, 'name'>>;

/** PATCH /api/registry/categories/:id. */
export type PatchCategoryBody = Versioned<EntityPatch<Category>>;

// -- Statuses (the managed dictionary, F-01/F-23) ----------------------------

/** POST /api/registry/statuses — creates a CUSTOM status (`st-…` id). */
export interface CreateStatusBody {
  version: number;
  label: string;
}

/** PATCH /api/registry/statuses/:id — rename (system statuses included). */
export interface PatchStatusBody {
  version: number;
  label: string;
}

/**
 * POST /api/registry/dictionary-positions — the whole new order of ONE managed
 * dictionary (statuses | streams | categories); the shared
 * `DictionaryPositionsPatch` write protocol.
 */
export type DictionaryPositionsBody = DictionaryPositionsPatch;

// -- Audit -------------------------------------------------------------------

export interface AuditQuery {
  /** History of one project/entity. */
  entityId?: string;
  /** Substring, case-insensitive, on actor email OR name. */
  actor?: string;
  /** 1..200, server default 50. */
  limit?: number;
  offset?: number;
}

/** `GET /api/registry/audit` — `total` counts the whole filter, not this page. */
export interface AuditPageResponse {
  items: AuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

// -- Registry ---------------------------------------------------------------

export const getRegistry = (signal?: AbortSignal) => request<RegistryDocument>('GET', '/registry', { signal });

export const createProject = (body: CreateProjectBody) =>
  request<RegistryDocument>('POST', '/registry/projects', jsonBody('POST', body));

export const patchProject = (id: string, body: PatchProjectBody) =>
  request<RegistryDocument>('PATCH', `/registry/projects/${encodeURIComponent(id)}`, jsonBody('PATCH', body));

/** F-25 — one drag sends the stream's whole new order; the answer replaces the cache. */
export const setPositions = (body: PositionsBody) =>
  request<RegistryDocument>('POST', '/registry/positions', jsonBody('POST', body));

/** The team roster's manual order — same full-list contract as `setPositions`. */
export const setTeamPositions = (body: TeamPositionsBody) =>
  request<RegistryDocument>('POST', '/registry/team-positions', jsonBody('POST', body));

/** One person's own queue manual order — same full-list contract. */
export const setPersonPositions = (body: PersonPositionsBody) =>
  request<RegistryDocument>('POST', '/registry/people-positions', jsonBody('POST', body));

/** F-28 — one drop/«наверх» sends the WHOLE registry order; the answer replaces the cache. */
export const setAllPositions = (body: AllPositionsBody) =>
  request<RegistryDocument>('POST', '/registry/all-positions', jsonBody('POST', body));

export const deleteProject = (id: string, version: number) =>
  request<RegistryDocument>('DELETE', `/registry/projects/${encodeURIComponent(id)}`, jsonBody('DELETE', { version } satisfies DeleteBody));

// -- Dictionaries -----------------------------------------------------------

/**
 * Deleting a person cascades: every assignment pointing at them is removed in
 * the same transaction and each touched project is audited (spec §1.1).
 */
export const createPerson = (body: CreatePersonBody) =>
  request<RegistryDocument>('POST', '/registry/people', jsonBody('POST', body));

export const patchPerson = (id: string, body: PatchPersonBody) =>
  request<RegistryDocument>('PATCH', `/registry/people/${encodeURIComponent(id)}`, jsonBody('PATCH', body));

export const deletePerson = (id: string, version: number) =>
  request<RegistryDocument>('DELETE', `/registry/people/${encodeURIComponent(id)}`, jsonBody('DELETE', { version } satisfies DeleteBody));

export const createTeam = (body: CreateTeamBody) =>
  request<RegistryDocument>('POST', '/registry/teams', jsonBody('POST', body));

export const patchTeam = (id: string, body: PatchTeamBody) =>
  request<RegistryDocument>('PATCH', `/registry/teams/${encodeURIComponent(id)}`, jsonBody('PATCH', body));

/** Deleting a team is REFUSED (422) while it still has people — move them first. */
export const deleteTeam = (id: string, version: number) =>
  request<RegistryDocument>('DELETE', `/registry/teams/${encodeURIComponent(id)}`, jsonBody('DELETE', { version } satisfies DeleteBody));

export const createStream = (body: CreateStreamBody) =>
  request<RegistryDocument>('POST', '/registry/streams', jsonBody('POST', body));

export const patchStream = (id: string, body: PatchStreamBody) =>
  request<RegistryDocument>('PATCH', `/registry/streams/${encodeURIComponent(id)}`, jsonBody('PATCH', body));

/** Deleting a stream is REFUSED (422) while projects reference it. */
export const deleteStream = (id: string, version: number) =>
  request<RegistryDocument>('DELETE', `/registry/streams/${encodeURIComponent(id)}`, jsonBody('DELETE', { version } satisfies DeleteBody));

export const createCategory = (body: CreateCategoryBody) =>
  request<RegistryDocument>('POST', '/registry/categories', jsonBody('POST', body));

export const patchCategory = (id: string, body: PatchCategoryBody) =>
  request<RegistryDocument>('PATCH', `/registry/categories/${encodeURIComponent(id)}`, jsonBody('PATCH', body));

/** Deleting a category nulls the project references («не задана») — with audit. */
export const deleteCategory = (id: string, version: number) =>
  request<RegistryDocument>('DELETE', `/registry/categories/${encodeURIComponent(id)}`, jsonBody('DELETE', { version } satisfies DeleteBody));

// -- Statuses (the managed dictionary) ---------------------------------------

export const createStatus = (body: CreateStatusBody) =>
  request<RegistryDocument>('POST', '/registry/statuses', jsonBody('POST', body));

export const patchStatus = (id: string, body: PatchStatusBody) =>
  request<RegistryDocument>('PATCH', `/registry/statuses/${encodeURIComponent(id)}`, jsonBody('PATCH', body));

/** Refused (422) for system statuses and for statuses projects still use. */
export const deleteStatus = (id: string, version: number) =>
  request<RegistryDocument>('DELETE', `/registry/statuses/${encodeURIComponent(id)}`, jsonBody('DELETE', { version } satisfies DeleteBody));

/** The full new order of one dictionary — array order is the display order. */
export const setDictionaryPositions = (body: DictionaryPositionsBody) =>
  request<RegistryDocument>('POST', '/registry/dictionary-positions', jsonBody('POST', body));

// -- Systems (F-21, managed since 2026-10-14) --------------------------------

/** POST /api/registry/systems — `name` required; `ownerPersonId` must exist (null = none). */
export type CreateSystemBody = Versioned<
  Pick<SystemEntry, 'name'> & Partial<Omit<SystemEntry, 'id' | 'name'>> & { id?: string }
>;

/** PATCH /api/registry/systems/:id — flat diff; `archived` is a plain boolean toggle. */
export type PatchSystemBody = Versioned<EntityPatch<SystemEntry>>;

export const createSystem = (body: CreateSystemBody) =>
  request<RegistryDocument>('POST', '/registry/systems', jsonBody('POST', body));

export const patchSystem = (id: string, body: PatchSystemBody) =>
  request<RegistryDocument>('PATCH', `/registry/systems/${encodeURIComponent(id)}`, jsonBody('PATCH', body));

/**
 * Deleting a system is REFUSED (422 `invalid_ref`, the count in the message)
 * while projects link it — archive it instead; delete after unlinking.
 */
export const deleteSystem = (id: string, version: number) =>
  request<RegistryDocument>('DELETE', `/registry/systems/${encodeURIComponent(id)}`, jsonBody('DELETE', { version } satisfies DeleteBody));

// -- Audit ------------------------------------------------------------------

export const getAudit = (query: AuditQuery = {}, signal?: AbortSignal) =>
  request<AuditPageResponse>('GET', `/registry/audit${buildQuery(query)}`, { signal });
